import { FASTElement, customElement, observable } from '@microsoft/fast-element';
import { assignedUsersTemplate } from './assigned-users.template';
import { assignedUsersStyles } from './assigned-users.styles';
import { ZeroGridPro } from '@genesislcap/foundation-zero-grid-pro';
import { IUserService } from '../../../../../services/user.service';
import { ICompany, IUserCompany, IValidProfileByCompanyRole } from '../../../../../types';
import { updateArray } from '../../../../../utils/dataserver';
import { Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { ICompanyService } from '../../../../../services/company.service';
import { COLUMN_FIELDS } from './column-config';
import { COMPANY_ROLE } from '../edit';
import { PROFILE_RIGHTS } from '../../../../../utils/enums/generic.enum';
import { Profile } from '../../../../../utils/profiles';

@customElement({
  name: 'assigned-users',
  template: assignedUsersTemplate,
  styles: assignedUsersStyles,
})
export class AssignedUsers extends FASTElement {
  @IUserService userService: IUserService;
  @ICompanyService companyServer: ICompanyService;
  @observable assignedUsersGrid: ZeroGridPro;
  @observable userCompanies: Array<IUserCompany> = [];
  @observable users: Array<any> = [];
  @observable profileUsers: Array<any> = [];
  @observable userRights: Array<any> = [];
  @observable showAssignedUsers: boolean = false;
  @observable isUserSelected: boolean = false;
  @observable company: ICompany;
  @observable searchUsersValue: string = '';

  @observable userCompanySubscription: SocketSubscription;

  private validProfileByCompanyRole: Array<IValidProfileByCompanyRole> = [];

  public connectedCallback() {
    super.connectedCallback();

    const gridOptions = {
      onGridReady: (grid) => {
        grid.api.addEventListener('rowSelected', () => {
          this.checkSelected();
        });
      },
      animateRows: true,
      suppressRowClickSelection: true,
      suppressColumnMoveAnimation: true,
      suppressCellFocus: true,
      suppressBrowserResizeObserver: true,
      scrollbarWidth: 10,
      defaultColDef: {
        resizable: true,
        wrapHeaderText: true,
        autoHeaderHeight: true,
      },
    };

    this.assignedUsersGrid.gridOptions = gridOptions;

    this.loadInitialData();
  }

  public checkSelected() {
    if (!this.assignedUsersGrid?.gridApi) {
      return;
    }
    let selectedUsers = this.assignedUsersGrid.gridApi.getSelectedRows();
    this.isUserSelected = selectedUsers.length > 0;
  }

  public setCompany(company: ICompany) {
    this.company = company;
    this.userCompanies = [];
    this.$emit('companyHasUser', false);
    this.showAssignedUsers = true;
    this.searchUsersValue = '';
    this.clearFilters();
    this.setUserData();
    this.getUserData(this.company);
  }

  public showAssignedUsersChanged(oldValue: boolean, newValue: boolean) {
    if (!this.assignedUsersGrid?.columnApi) {
      return;
    }
    if (newValue) {
      this.assignedUsersGrid.columnApi.setColumnVisible(COLUMN_FIELDS.UNASSIGN_USER, true);
    } else {
      this.assignedUsersGrid.columnApi.setColumnVisible(COLUMN_FIELDS.UNASSIGN_USER, false);
    }
  }

  public setUserData(filter: string = '') {
    if (this.assignedUsersGrid) {
      if (this.showAssignedUsers) {
        this.assignedUsersGrid.gridApi?.setRowData(this.userCompanies);
      } else {
        const validProfilesForCompanyRole = this.validProfileByCompanyRole
          .filter((profile) => profile.COMPANY_ROLE === this.company?.COMPANY_ROLE)
          .map((profile) => profile.PROFILE_NAME)
          .concat(Profile.CASCADE_ADMIN);

        const filteredList = this.users.filter((user) => {
          if (user.STATUS === 'DISABLED') {
            return false;
          }

          const userProfilesForUser = this.profileUsers
            .filter((profile) => profile.USER_NAME === user.USER_NAME)
            .map((profile) => profile.PROFILE_NAME);

          if (
            !userProfilesForUser.every((profile) => validProfilesForCompanyRole.includes(profile))
          ) {
            return false;
          }

          if (this.company?.COMPANY_ROLE === COMPANY_ROLE.ADVISOR) {
            const isAdvisor = this.userRights.find(
              (elm) =>
                user.USER_NAME === elm.USER_NAME && elm.RIGHT_CODE === PROFILE_RIGHTS.ADVISOR,
            );

            const isAdmin = this.userRights.find(
              (elm) =>
                user.USER_NAME === elm.USER_NAME &&
                elm.RIGHT_CODE === PROFILE_RIGHTS.ADMIN_VISIBILITY,
            );
            if (isAdmin) {
              return true;
            }

            //only show advisor users that don't already have an advisor company
            if (!isAdvisor || user.ADVISOR_COMPANY_ID) {
              return false;
            }
          }

          // Check if the element is in list2
          const isUserAssigned = this.userCompanies.some(
            (userCompany) => userCompany.USER_NAME === user.USER_NAME,
          );

          let isInSearch = true;
          if (filter) {
            isInSearch =
              user.USER_NAME.toLocaleLowerCase().includes(filter.toLocaleLowerCase()) ||
              (user.FIRST_NAME &&
                user.FIRST_NAME.toLocaleLowerCase().includes(filter.toLocaleLowerCase())) ||
              (user.LAST_NAME &&
                user.LAST_NAME.toLocaleLowerCase().includes(filter.toLocaleLowerCase())) ||
              (user.EMAIL_ADDRESS &&
                user.EMAIL_ADDRESS.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
          }
          // If the element is not in list2, return true
          return !isUserAssigned && isInSearch;
        });
        this.assignedUsersGrid.gridApi?.setRowData(filteredList);
      }
    }
  }

  public loadInitialData() {
    this.userService.getAllUserDetails().subscribe({
      next: (streamMessage: Message) => {
        const result = streamMessage.ROW ?? null;
        if (result) {
          this.users = updateArray(result, this.users);
          this.setUserData();
        }
      },
      error: (error: any) => {
        this.userCompanies = [];
        this.$emit('companyHasUser', false);
      },
    });

    this.userService.getAllProfileUsers().subscribe({
      next: (streamMessage: Message) => {
        const result = streamMessage.ROW ?? null;
        if (result) {
          this.profileUsers = updateArray(result, this.profileUsers);
        }
      },
      error: (error: any) => {
        this.userCompanies = [];
        this.$emit('companyHasUser', false);
      },
    });

    this.userService.getAllUserRights().subscribe({
      next: (streamMessage: Message) => {
        const result = streamMessage.ROW ?? null;
        if (result) {
          this.userRights = updateArray(result, this.userRights);
        }
      },
      error: (error: any) => {
        this.userCompanies = [];
        this.$emit('companyHasUser', false);
      },
    });

    this.userService.getAllValidProfileByCompanyRole().then((data) => {
      this.validProfileByCompanyRole = data.REPLY;
    });
  }

  public getUserData(company: ICompany) {
    this.userCompanySubscription?.unsubscribe();
    if (company && company.CASCADE_COMPANY_ID) {
      let companyId = company.CASCADE_COMPANY_ID;
      if (company.IS_SPV_FLAG && company.CASCADE_PARENT_COMPANY_ID) {
        companyId = company.CASCADE_PARENT_COMPANY_ID;
      }
      this.userCompanySubscription = this.userService.getAllCompanyUsers(companyId).subscribe({
        next: (streamMessage: Message) => {
          const result = streamMessage.ROW ?? null;

          if (this.assignedUsersGrid && result) {
            this.userCompanies = updateArray(result, this.userCompanies).filter((x) => x.USER_NAME);
            this.$emit('companyHasUser', this.userCompanies.length > 0);
            this.setUserData();
          }
        },
        error: (error: any) => {
          this.userCompanies = [];
          this.$emit('companyHasUser', false);
        },
      });
    } else {
      this.showAssignedUsers = false;
      this.setUserData();
    }
  }

  public unassignSelectedUsers() {
    const selectedUsers: IUserCompany[] = this.assignedUsersGrid.gridApi.getSelectedRows();
    this.unassignUsers(selectedUsers.map((user) => user.USER_NAME));
  }

  public unassignUser(userName: string) {
    this.unassignUsers([userName]);
  }

  public unassignUsers(userNames: string[]) {
    this.companyServer
      .eventUnassignCompanyUsers({
        CASCADE_COMPANY_ID: this.company.CASCADE_COMPANY_ID,
        USER_NAMES: userNames,
      })
      .then((response) => {
        if (response.MESSAGE_TYPE === 'EVENT_ACK') {
          this.assignedUsersGrid.gridApi.deselectAll();
        }
      });
  }

  public assignMoreUsers() {
    this.assignedUsersGrid.gridApi.deselectAll();
    this.clearFilters();
    this.showAssignedUsers = false;
    this.setUserData();
  }

  private clearFilters() {
    this.assignedUsersGrid.gridApi.setFilterModel(null);
    this.assignedUsersGrid.gridApi.onFilterChanged();
  }

  public assignSelectedUsers() {
    const selectedUsers = this.assignedUsersGrid.gridApi.getSelectedRows();
    if (selectedUsers?.length > 0) {
      this.companyServer
        .eventAssignCompanyUsers({
          CASCADE_COMPANY_ID: this.company.CASCADE_COMPANY_ID,
          USER_NAMES: selectedUsers.map((user) => user.USER_NAME),
        })
        .then((response) => {
          if (response.MESSAGE_TYPE === 'EVENT_ACK') {
            this.assignedUsersGrid.gridApi.deselectAll();
            this.clearFilters();
            this.showAssignedUsers = true;
            this.searchUsersValue = '';
            this.isUserSelected = false;
            this.setUserData();
          }
        });
    } else {
      this.assignedUsersGrid.gridApi.deselectAll();
      this.clearFilters();
      this.showAssignedUsers = true;
      this.searchUsersValue = '';
      this.isUserSelected = false;
      this.setUserData();
    }
  }

  public downloadSelected() {
    const csvParams = {
      onlySelected: true,
      fileName: `${this.company.NAME} Users`,
      processCellCallback: (params) => {
        let formattedValue = params.value;

        if (params.column.getColId() === COLUMN_FIELDS.STATUS) {
          formattedValue = JSON.parse(params.value).STATUS;
          const companiesLinked = JSON.parse(params.value).COMPANIES_LINKED;
          if (formattedValue === 'ENABLED' && companiesLinked > 1) {
            formattedValue = 'ENABLED_MULTIPLE_COMPANIES';
          }
        }

        return formattedValue;
      },
    };
    this.assignedUsersGrid.gridApi.exportDataAsCsv(csvParams);
  }
}
